import React from "react";
import { getCategory } from "@/lib/categories";

// Futuristic crab-robot avatar — shared CLAWNET visual DNA, tinted per agent.
export const AgentAvatar = ({
  color,
  category = "Research",
  size = 48,
  status,
  showRing = true,
  className = "",
  floating = false,
}) => {
  const c = color || getCategory(category).hex;
  const uid = React.useId().replace(/:/g, "");
  const dim = typeof size === "number" ? `${size}px` : size;

  return (
    <div
      className={`relative inline-flex items-center justify-center ${floating ? "animate-float" : ""} ${className}`}
      style={{ width: dim, height: dim }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 100 100" width="100%" height="100%" role="img">
        <defs>
          <radialGradient id={`body-${uid}`} cx="50%" cy="38%" r="70%">
            <stop offset="0%" stopColor="#1a2238" />
            <stop offset="100%" stopColor="#0a0f1e" />
          </radialGradient>
          <linearGradient id={`claw-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
            <stop offset="100%" stopColor={c} />
          </linearGradient>
          <filter id={`glow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.2" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {showRing && (
          <circle cx="50" cy="50" r="47" fill="none" stroke={c} strokeOpacity="0.4" strokeWidth="2" />
        )}
        <circle cx="50" cy="50" r="42" fill="#070b16" />

        {/* claws */}
        <path d="M30 40 C20 30 18 18 26 14 C30 26 34 30 38 34 Z" fill={`url(#claw-${uid})`} />
        <path d="M70 40 C80 30 82 18 74 14 C70 26 66 30 62 34 Z" fill={`url(#claw-${uid})`} />
        {/* side legs */}
        <path d="M24 56 L14 60 M24 64 L15 70" stroke={c} strokeWidth="2.4" strokeLinecap="round" opacity="0.7" />
        <path d="M76 56 L86 60 M76 64 L85 70" stroke={c} strokeWidth="2.4" strokeLinecap="round" opacity="0.7" />

        {/* head/body */}
        <rect x="30" y="34" width="40" height="36" rx="16" fill={`url(#body-${uid})`} stroke={c} strokeOpacity="0.55" strokeWidth="1.6" />
        {/* eyes */}
        <g filter={`url(#glow-${uid})`}>
          <rect x="39" y="47" width="7" height="12" rx="3.5" fill={c} />
          <rect x="54" y="47" width="7" height="12" rx="3.5" fill={c} />
        </g>
        {/* mouth line */}
        <rect x="42" y="63" width="16" height="2.4" rx="1.2" fill={c} opacity="0.5" />
      </svg>

      {status && (
        <span className="absolute bottom-0 right-0 flex h-3 w-3">
          <span className={`absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping ${statusDot(status)}`} />
          <span className={`relative inline-flex h-3 w-3 rounded-full ring-2 ring-[#050814] ${statusDot(status)}`} />
        </span>
      )}
    </div>
  );
};

function statusDot(status) {
  switch (status) {
    case "WORKING": return "bg-cyan-400";
    case "COLLABORATING": return "bg-violet-400";
    case "VERIFYING": return "bg-amber-400";
    case "OFFLINE": return "bg-slate-500";
    default: return "bg-emerald-400";
  }
}

export default AgentAvatar;
